import "./TableViewData.scss";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Radio,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RootState } from "../../../../store/reducers";
import { cloneDeep } from "lodash";
import {
  JobPricingPercentileData,
  JobPricingPercentileDetails,
  JobPricingReportDetails
} from "../../../../helpers/models/JobPricingDetailModels";
import { JobPricingReportConstants } from "../../../../constants/JobPricingReportConstants";
import {
  getReportDetailsList,
  setDefaultTargetData,
  updateReportDetails
} from "../../../../store/slices/jobPricingDetailsSlice";
import { updateJobPricingPayloads } from "../../../../store/slices/jobPricingSearchSlice";
import { useTranslation } from "react-i18next";

export const TableViewData = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    percentileDataList,
    reportDetails,
    targetData,
    showTableCompElements,
    showSelectedPercentileList,
    isReportDetailsLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const { jobPricingPayloads } = useSelector(
    (state: RootState) => state.jobPricingSearch
  );
  const [percentileHeaderList, setPercentileHeaderList] = useState<string[]>([
    ...JobPricingReportConstants.DEFAULT_PERCENTILE_LABELS,
    JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_LABELS
  ]);
  const [percentileHeaderValues, setPercentileHeaderValues] = useState<
    string[]
  >([
    ...JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES,
    JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
  ]);

  const updatedReportDetails = (selectedTarget: JobPricingPercentileData) => {
    const existingPercentileData = jobPricingPayloads.marketPosition.split(",");
    if (existingPercentileData.includes(selectedTarget.percentile)) {
      const updatedReportDetails = getReportDetailsList(
        cloneDeep(reportDetails),
        selectedTarget
      );
      dispatch(updateReportDetails(updatedReportDetails));
    } else {
      const selectedPercentileData = showSelectedPercentileList.map(
        (data: JobPricingPercentileData) => data.percentile
      );
      const updatedJobPricingPayloads = {
        marketPosition: [...selectedPercentileData, selectedTarget.percentile]
          .sort()
          .join()
      };
      dispatch(updateJobPricingPayloads(updatedJobPricingPayloads));
    }
  };

  const renderTargetDataDropDown = () => {
    return (
      <Autocomplete
        id="percentile-data-list"
        classes={{ popper: "MuiAutocompletePopper-TargetData" }}
        size="small"
        disableClearable
        options={percentileDataList}
        getOptionLabel={(option) => option.percentileDisplay}
        renderTags={() => {
          return null;
        }}
        isOptionEqualToValue={(option, value) =>
          option.percentileDisplay === value.percentileDisplay
        }
        value={targetData || {}}
        onChange={(event: any, value: JobPricingPercentileData) => {
          dispatch(setDefaultTargetData(value));
          updatedReportDetails(value);
        }}
        renderOption={(propsHTML, option, { selected }) => {
          return (
            <li {...propsHTML} key={option.percentile}>
              <Radio
                style={{ marginRight: 4 }}
                color="secondary"
                checked={
                  option.percentileDisplay ===
                  (targetData?.percentileDisplay || "")
                    ? true
                    : false
                }
              />
              {option.percentileDisplay}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={targetData?.percentileDisplay || ""}
          />
        )}
        popupIcon={<ExpandMoreIcon />}
        sx={{
          width: 75,
          ".MuiOutlinedInput-root": { fontSize: "14px !important" },
          ".MuiInputBase-sizeSmall": { padding: "0px !important" },
          ".MuiAutocomplete-input": {
            padding: "2px 0 0 8px !important",
            fontWeight: 700
          },
          ".MuiOutlinedInput-notchedOutline": {
            border: "0px !important"
          }
        }}
      />
    );
  };

  const renderPercentileHeader = () => {
    return (
      <>
        {percentileHeaderList.map((value: string, index: number) => (
          <TableCell key={index}>{value}</TableCell>
        ))}
      </>
    );
  };

  const renderTableLoader = () => {
    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell>{t("pa.element")}</TableCell>
            <TableCell sx={{ padding: "6px 16px !important" }}>
              {t("pa.target")}
            </TableCell>
            <TableCell sx={{ padding: "6px 16px !important" }}>
              {t("pa.myOrgavg")}
            </TableCell>
            <TableCell>{t("pa.myOrg")}</TableCell>
            {renderPercentileHeader()}
            <TableCell>{t("pa.sjpr.incumbents")}</TableCell>
            <TableCell>{t("pa.sjpr.organizations")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3, 4, 5].map((value: number, index: number) => (
            <TableRow key={"table-data-loading-" + index}>
              <TableCell scope="row">
                <Skeleton />
              </TableCell>
              <TableCell scope="row" sx={{ padding: "6px 16px !important" }}>
                <Skeleton />
              </TableCell>
              <TableCell scope="row" sx={{ padding: "6px 16px !important" }}>
                <Skeleton />
              </TableCell>
              <TableCell scope="row">
                <Skeleton />
              </TableCell>
              {percentileHeaderValues.map((value: string, index: number) => (
                <TableCell key={index} scope="row">
                  <Skeleton />
                </TableCell>
              ))}
              <TableCell scope="row">
                <Skeleton />
              </TableCell>
              <TableCell scope="row">
                <Skeleton />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  useEffect(() => {
    if (showSelectedPercentileList && showSelectedPercentileList.length > 0) {
      setPercentileHeaderList(
        showSelectedPercentileList.map(
          (data: JobPricingPercentileData) => data.percentileDisplay
        )
      );
      setPercentileHeaderValues(
        showSelectedPercentileList.map(
          (data: JobPricingPercentileData) => data.percentile
        )
      );
    } else {
      setPercentileHeaderList([
        ...JobPricingReportConstants.DEFAULT_PERCENTILE_LABELS,
        JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_LABELS
      ]);
      setPercentileHeaderValues([
        ...JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES,
        JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
      ]);
    }
  }, [showSelectedPercentileList]);
  return (
    <div className="table-view-data-container">
      <TableContainer>
        <Table
          stickyHeader
          size="small"
          aria-label="SJPR Table Details"
          sx={{ whiteSpace: "nowrap" }}
        >
          {isReportDetailsLoading ? (
            renderTableLoader()
          ) : (
            <>
              <TableHead>
                <TableRow>
                  <TableCell>{t("pa.element")}</TableCell>
                  <TableCell>
                    <div className="cell-bg-color target-data-list">
                      <span>{t("pa.target")}: </span>
                      {renderTargetDataDropDown()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="cell-bg-color">{t("pa.myOrgavg")}</div>
                  </TableCell>
                  <TableCell>
                    {t("pa.myOrg")} {targetData?.percentileDisplay || ""}
                  </TableCell>
                  {renderPercentileHeader()}
                  <TableCell>{t("pa.sjpr.incumbents")}</TableCell>
                  <TableCell>{t("pa.sjpr.organizations")}</TableCell>
                </TableRow>
              </TableHead>
              {reportDetails.length > 0 && (
                <TableBody>
                  {reportDetails
                    .filter((data: JobPricingReportDetails, index: number) =>
                      showTableCompElements.includes(data.factName) && data.marketCount && data.marketCount > 0
                    )
                    .map((data: JobPricingReportDetails, index: any) => (
                      <TableRow key={"table-data-" + index}>
                        <TableCell component="th" scope="row">
                          {data.factName}
                        </TableCell>
                        <TableCell>
                          <div className="cell-bg-color">
                            {(data.targetMarket || "-").toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="cell-bg-color">
                            {(data.avgOrg || "-").toLocaleString()}&nbsp;&nbsp;
                            {data.avgOrgPercentage &&
                            data.avgOrgPercentage > 0 ? (
                              <Tooltip
                                PopperProps={{
                                  disablePortal: true
                                }}
                                title={"This data is based on market data"}
                                placement="right"
                                arrow
                              >
                                <span
                                  style={{
                                    color:
                                      data.avgOrgPercentage > 100
                                        ? "green"
                                        : "red"
                                  }}
                                >
                                  ({data.avgOrgPercentage}%)
                                </span>
                              </Tooltip>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(data.targetOrg || "-").toLocaleString()}
                        </TableCell>
                        {data.percentileDetails
                          ?.filter(
                            (
                              percentileData: JobPricingPercentileDetails,
                              index: number
                            ) =>
                              percentileHeaderValues.includes(
                                percentileData.percentileValue
                              )
                          )
                          .map(
                            (
                              percentileData: JobPricingPercentileDetails,
                              index: number
                            ) => (
                              <TableCell key={index}>
                                {(
                                  percentileData.marketValue || "-"
                                ).toLocaleString()}
                              </TableCell>
                            )
                          )}
                        <TableCell>{data.incumbents || "-"}</TableCell>
                        <TableCell>{data.orgs || "-"}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
              {reportDetails.length === 0 && (
                <caption style={{ textAlign: "center" }}>
                  {JobPricingReportConstants.NO_DATA_AVAILABLE_MSG}
                </caption>
              )}
            </>
          )}
        </Table>
      </TableContainer>
    </div>
  );
};
